import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, LogOut, MonitorSmartphone } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';

/**
 * Where you are signed in, and a way to end any of it. ADR-038.
 *
 * The answer to "I think somebody has my login" that does not need an admin:
 * see every device holding a session, recognise the ones that are yours, and
 * sign the rest out. Signing out other devices does not change your password —
 * the screen says so, because if the password is what leaked, that is the
 * next thing to do.
 */
export function DeviceSessions() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessions = useQuery({ queryKey: ['my-sessions'], queryFn: api.mySessions });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['my-sessions'] });

  const endOne = useMutation({
    mutationFn: (id: string) => api.endMySession(id),
    onSuccess: async () => {
      setNotice('That device has been signed out.');
      await refresh();
    },
    onError: (caught: Error) => setError(caught.message),
  });

  const endOthers = useMutation({
    mutationFn: api.endOtherSessions,
    onSuccess: async ({ ended }) => {
      setNotice(
        ended === 0
          ? 'You were not signed in anywhere else.'
          : `Signed out of ${ended} other ${ended === 1 ? 'device' : 'devices'}.`,
      );
      await refresh();
    },
    onError: (caught: Error) => setError(caught.message),
  });

  const rows = sessions.data?.sessions ?? [];
  const others = rows.filter((row) => !row.current).length;

  return (
    <section className="card space-y-4" aria-labelledby="device-sessions-heading">
      <div className="flex items-center gap-2">
        <MonitorSmartphone className="h-5 w-5 text-primary-600" aria-hidden="true" />
        <h3 id="device-sessions-heading" className="font-heading text-lg">
          Where you are signed in
        </h3>
      </div>

      <p className="text-sm text-muted-foreground">
        Every device that can act as you right now. If you do not recognise one, sign it out — and
        then change your password, because signing a device out does not change it.
      </p>

      {notice && (
        <p
          role="status"
          className="flex items-center gap-2 rounded-xl bg-secondary-50 p-3 text-sm text-secondary-700"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {sessions.isPending ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="font-medium">
                  {row.device}
                  {row.current && (
                    <span className="ml-2 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700">
                      This device
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Signed in {formatWhen(row.signedInAt)} · last used {formatWhen(row.lastSeenAt)}
                  {row.ip ? ` · from ${row.ip}` : ''}
                  {row.remembered ? ' · kept signed in' : ''}
                </p>
              </div>
              {!row.current && (
                <button
                  type="button"
                  className="btn-quiet text-sm"
                  disabled={endOne.isPending}
                  onClick={() => {
                    setNotice(null);
                    setError(null);
                    endOne.mutate(row.id);
                  }}
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="btn-primary"
        disabled={others === 0 || endOthers.isPending}
        onClick={() => {
          setNotice(null);
          setError(null);
          endOthers.mutate();
        }}
      >
        {endOthers.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Sign out all other devices
      </button>

      <p className="text-xs text-muted-foreground">
        For your safety, a session ends by itself after four hours with nothing open (two for an
        admin), and at the end of the working day — or after thirty days on a device where you
        ticked &ldquo;keep me signed in&rdquo; (seven for an admin). Addresses on a mobile network
        change often; a different town is worth a look, a different number on its own is not.
      </p>
    </section>
  );
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} minutes ago`;
  return date.toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' });
}
